import {
  FullShopArticle,
  useCreateArticle,
  useUpdateArticle,
} from "@/api/article";
import { BriefSellable } from "@/api/dtos";
import { MultilingualField } from "@/components/form-items/MultilingualField";
import StyledDrawer from "@/components/StyledDrawer";
import { Button } from "@/components/ui/button";
import { CustomDialogFooter } from "@/components/ui/custom-dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { SheetClose } from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/hooks/use-toast";
import { multilingualFieldSchema } from "@/i18n";
import { emptyStringToNull } from "@/lib/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { PencilIcon } from "lucide-react";
import { useEffect, useId, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import {
  tableRowActionMenuItemClassName,
} from "@/components/table/TableRowActionsMenu";
import "./innerHTMLStyle.css";

export interface CreateArticleDrawerProps {
  sellable: BriefSellable;
  variant: "create";
}

export interface EditArticleDrawerProps {
  article: FullShopArticle;
  variant: "edit";
  menuItemTrigger?: boolean;
  /** Controlled open state — required when the drawer is opened from outside (e.g. row actions menu). */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const baseSchema = z.object({
  priceAmount: z.coerce.number().positive(),
  additionalInfo: multilingualFieldSchema(
    z.string().nullable().transform(emptyStringToNull),
  ),
});

const editSchema = baseSchema.extend({
  currentQuantity: z.coerce.number().nonnegative(),
});

const submitSchema = baseSchema.extend({
  currentQuantity: z.coerce.number().positive(),
});

export const ArticleEditDrawer = (
  prop: CreateArticleDrawerProps | EditArticleDrawerProps,
) => {
  const { t } = useTranslation();
  const creator = useCreateArticle();
  const editor = useUpdateArticle();
  const formId = useId();

  const sellable =
    prop.variant == "edit" ? prop.article.sellableContent : prop.sellable;

  const formSchema = prop.variant == "edit" ? editSchema : submitSchema;

  const defaultValues = useMemo(
    () => ({
      priceAmount:
        prop.variant == "edit" ? prop.article.priceAmount / 100 : undefined,
      currentQuantity:
        prop.variant == "edit" ? prop.article.currentQuantity : 1,
      additionalInfo:
        prop.variant == "edit" ? prop.article.additionalInfo : undefined,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [prop.variant, "article" in prop && prop.article],
  );

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues,
    mode: "onTouched",
  });

  useEffect(() => {
    form.reset(defaultValues);
  }, [defaultValues, form]);

  const isExternallyControlled =
    prop.variant === "edit" && prop.onOpenChange != null;
  const [internalOpen, setInternalOpen] = useState(false);
  const open = isExternallyControlled ? (prop.open ?? false) : internalOpen;
  const setOpen = isExternallyControlled
    ? prop.onOpenChange!
    : setInternalOpen;

  function editOnSubmit(values: z.infer<typeof formSchema>) {
    if (prop.variant != "edit") return;

    editor.mutate(
      {
        articleId: prop.article.id,
        updateArticle: {
          additionalInfo: values.additionalInfo,
          currentQuantity: values.currentQuantity,
          priceAmount: Math.round(values.priceAmount * 100),
        },
      },
      {
        onSuccess: () => {
          toast({
            title: t("success"),
            description: t("editArticleSucessMessage"),
            duration: 1000,
            variant: "success",
          });
          setOpen(false);
        },
        onError: () =>
          toast({
            title: t("error"),
            description: t("editArticleErrorMessage"),
            duration: 1000,
            variant: "error",
          }),
      },
    );

    setOpen(false);
  }

  function createOnSubmit(values: z.infer<typeof formSchema>) {
    if (prop.variant != "create") return;
    creator.mutate(
      {
        additionalInfo: values.additionalInfo,
        currentQuantity: values.currentQuantity,
        priceAmount: Math.round(values.priceAmount * 100),
        sellableId: prop.sellable.id,
        itemType: prop.sellable.itemType,
      },
      {
        onSuccess: () => {
          toast({
            title: t("success"),
            description: t("addArticleSucessMessage"),
            duration: 1000,
            variant: "success",
          });
          form.reset();
          setOpen(false);
        },
        onError: () =>
          toast({
            title: t("error"),
            description: t("addArticleErrorMessage"),
            duration: 1000,
            variant: "error",
          }),
      },
    );
    form.reset();
    setOpen(false);
  }

  const trigger = isExternallyControlled
    ? undefined
    : prop.variant == "create" ? (
        <Button variant={"link"}>
          {t(`eshop.addItem.${prop.sellable.itemType}.button`)}
        </Button>
      ) : prop.menuItemTrigger ? (
        <Button
          type="button"
          variant="ghost"
          className={tableRowActionMenuItemClassName}
        >
          <PencilIcon className="h-4 w-4 shrink-0 text-[#64748B]" aria-hidden />
          {t("edit")}
        </Button>
      ) : (
        <Button variant={"link"}>
          <PencilIcon className="text-gray-500" />
        </Button>
      );

  return (
    <StyledDrawer
      title={t("putOnSale")}
      trigger={trigger}
      footer={
        <CustomDialogFooter className="w-full">
          <SheetClose className="w-full" asChild>
            <Button className="w-full font-semibold" variant={"secondary"}>
              {t("cancel")}
            </Button>
          </SheetClose>
          <Button
            disabled={!form.formState.isValid}
            type="submit"
            form={formId}
            className="w-full font-semibold"
          >
            {prop.variant == "edit" ? t("update") : t("putOnSale")}
          </Button>
        </CustomDialogFooter>
      }
      className="md:max-w-[475px]"
      sheetProps={{ open, onOpenChange: setOpen }}
      description={
        prop.variant === "edit"
          ? t("TRAD_article_edit_dialog_description")
          : t("TRAD_article_create_dialog_description")
      }
    >
      <Form {...form}>
        <form
          id={formId}
          onSubmit={form.handleSubmit(
            prop.variant == "edit" ? editOnSubmit : createOnSubmit,
          )}
          className="flex flex-col gap-6 text-xs"
        >
          {sellable.previews && sellable.previews.length > 0 ? (
            <img
              src={sellable.previews[0].medium ?? ""}
              className="w-full aspect-[3/2] object-contain"
            ></img>
          ) : (
            <div className="w-full aspect-[3/2] object-contain bg-slate-400"></div>
          )}
          <div>
            <span className="text-gray-600">{t("registrationNumber")} </span>
            <i className="font-medium text-black">{sellable.recordingNumber}</i>
          </div>
          <div
            className="text-xs"
            dangerouslySetInnerHTML={{ __html: sellable.displayInfos }}
          ></div>
          <div> </div>
          <div className="flex flex-row gap-5">
            <FormField
              control={form.control}
              name="currentQuantity"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("quantity")}</FormLabel>
                  <FormControl>
                    <Input className="bg-white" type="number" {...field} />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="priceAmount"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("pricePerUnit")} €</FormLabel>
                  <FormControl>
                    <Input type="text" placeholder="20 000" {...field} />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
          <MultilingualField
            render={(lang) => (
              <FormField
                control={form.control}
                name={`additionalInfo.${lang}`}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("additionalInformation")}</FormLabel>
                    <FormControl>
                      <Textarea
                        className="min-h-40"
                        {...field}
                        value={field.value ?? undefined}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            )}
          />
        </form>
      </Form>
    </StyledDrawer>
  );
};
